import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Lock, LockOpen, X, Plus, User, Eye, EyeOff, Pencil, Image, MapPin, Mail, Phone, MessageSquare, Heart, Coffee, Popcorn, Quote } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { ProfileSettings, ProfileFieldSettings, User as UserType, UserProfile } from "@shared/schema";

type UserWithProfile = UserType & { profile?: UserProfile };

type FieldVisibility = "hidden" | "visible" | "editable";

interface ProfileField {
  key: string;
  label: string;
  icon: any;
  description: string;
}

const PROFILE_FIELDS: ProfileField[] = [
  { key: "profilePicture", label: "Profile Picture", icon: Image, description: "User's profile photo" },
  { key: "title", label: "Job Title", icon: User, description: "Custom job title display" },
  { key: "location", label: "Location", icon: MapPin, description: "City or office location" },
  { key: "bioShort", label: "Short Bio", icon: MessageSquare, description: "Brief description for cards" },
  { key: "bioLong", label: "Full Bio", icon: MessageSquare, description: "Detailed about section" },
  { key: "contactEmail", label: "Contact Email", icon: Mail, description: "Public contact email" },
  { key: "contactPhone", label: "Contact Phone", icon: Phone, description: "Public phone number" },
  { key: "socialLinks", label: "Social Links", icon: MessageSquare, description: "Instagram, TikTok, etc." },
  { key: "hobbies", label: "Hobbies", icon: Heart, description: "Personal interests" },
  { key: "favoriteQuote", label: "Favorite Quote", icon: Quote, description: "Inspirational quote" },
  { key: "favoriteMovieShow", label: "Favorite Movie/Show", icon: Popcorn, description: "Entertainment preference" },
  { key: "coffeeOrder", label: "Coffee Order", icon: Coffee, description: "Go-to coffee drink" },
  { key: "favoriteSnack", label: "Favorite Snack", icon: Coffee, description: "Preferred snack" },
];

const ROLES = [
  { value: "sales_rep", label: "Sales Rep" },
  { value: "artist", label: "Talent" },
  { value: "billing", label: "Billing" },
  { value: "admin", label: "Admin" },
  // v2: { value: "marketing", label: "Marketing" },
];

interface ProfileSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ProfileSettingsDialog({ open, onOpenChange }: ProfileSettingsDialogProps) {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("general");
  const [selectedRole, setSelectedRole] = useState("sales_rep");
  const [titleLockEnabled, setTitleLockEnabled] = useState(false);
  const [whitelistedUsers, setWhitelistedUsers] = useState<string[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [fieldSettings, setFieldSettings] = useState<Record<string, FieldVisibility>>({});

  const { data: settings, isLoading: settingsLoading } = useQuery<ProfileSettings>({
    queryKey: ["/api/admin/profile-settings"],
    enabled: open,
  });

  const { data: users, isLoading: usersLoading } = useQuery<UserWithProfile[]>({
    queryKey: ["/api/users"],
    enabled: open,
  });

  const { data: roleFieldSettings, isLoading: fieldSettingsLoading, refetch: refetchFieldSettings } = useQuery<ProfileFieldSettings>({
    queryKey: ["/api/admin/profile-field-settings", selectedRole],
    enabled: open && activeTab === "fields",
  });

  useEffect(() => {
    if (settings) {
      setTitleLockEnabled(!settings.allowTitleEdit);
      setWhitelistedUsers(settings.titleEditWhitelist || []);
    }
  }, [settings]);

  useEffect(() => {
    if (roleFieldSettings) {
      const newSettings: Record<string, FieldVisibility> = {};
      PROFILE_FIELDS.forEach(field => {
        newSettings[field.key] = (roleFieldSettings as any)[field.key] || "editable";
      });
      setFieldSettings(newSettings);
    } else {
      // Default all to editable
      const defaultSettings: Record<string, FieldVisibility> = {};
      PROFILE_FIELDS.forEach(field => {
        defaultSettings[field.key] = "editable";
      });
      setFieldSettings(defaultSettings);
    }
  }, [roleFieldSettings]);

  useEffect(() => {
    if (open && activeTab === "fields") {
      refetchFieldSettings();
    }
  }, [selectedRole, open, activeTab, refetchFieldSettings]);

  const updateSettingsMutation = useMutation({
    mutationFn: async (data: { allowTitleEdit?: boolean; titleEditWhitelist?: string[] }) => {
      return apiRequest("PATCH", "/api/admin/profile-settings", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/profile-settings"] });
      toast({
        title: "Settings updated",
        description: "Profile settings have been saved.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update settings.",
        variant: "destructive",
      });
    },
  });

  const updateFieldSettingsMutation = useMutation({
    mutationFn: async (data: Record<string, FieldVisibility>) => {
      return apiRequest("PATCH", `/api/admin/profile-field-settings/${selectedRole}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/profile-field-settings", selectedRole] });
      toast({
        title: "Field settings updated",
        description: `Profile fields for ${ROLES.find(r => r.value === selectedRole)?.label} have been saved.`,
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update field settings.",
        variant: "destructive",
      });
    },
  });

  const handleTitleLockToggle = (checked: boolean) => {
    setTitleLockEnabled(checked);
    updateSettingsMutation.mutate({ allowTitleEdit: !checked });
  };

  const handleAddWhitelistUser = () => {
    if (selectedUserId && !whitelistedUsers.includes(selectedUserId)) {
      const newWhitelist = [...whitelistedUsers, selectedUserId];
      setWhitelistedUsers(newWhitelist);
      setSelectedUserId("");
      updateSettingsMutation.mutate({ titleEditWhitelist: newWhitelist });
    }
  };

  const handleRemoveWhitelistUser = (userId: string) => {
    const newWhitelist = whitelistedUsers.filter(id => id !== userId);
    setWhitelistedUsers(newWhitelist);
    updateSettingsMutation.mutate({ titleEditWhitelist: newWhitelist });
  };

  const handleFieldVisibilityChange = (fieldKey: string, visibility: FieldVisibility) => {
    const newSettings = { ...fieldSettings, [fieldKey]: visibility };
    setFieldSettings(newSettings);
    updateFieldSettingsMutation.mutate(newSettings);
  };

  const getUserById = (userId: string) => {
    return users?.find(u => u.id === userId);
  };

  const availableUsersForWhitelist = users?.filter(
    u => !whitelistedUsers.includes(u.id) && u.profile?.role !== "admin"
  ) || [];

  const isLoading = settingsLoading || usersLoading;

  const getVisibilityIcon = (visibility: FieldVisibility) => {
    switch (visibility) {
      case "hidden": return <EyeOff className="w-4 h-4 text-muted-foreground" />;
      case "visible": return <Eye className="w-4 h-4 text-blue-500" />;
      case "editable": return <Pencil className="w-4 h-4 text-green-500" />;
    }
  };

  const getVisibilityLabel = (visibility: FieldVisibility) => {
    switch (visibility) {
      case "hidden": return "Hidden";
      case "visible": return "View Only";
      case "editable": return "Editable";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full sm:max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Profile Settings
          </DialogTitle>
          <DialogDescription>
            Control how users can edit their personal profiles
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="general" data-testid="tab-general">General Settings</TabsTrigger>
            <TabsTrigger value="fields" data-testid="tab-fields">Field Visibility</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="flex-1 overflow-y-auto mt-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
                  <div className="flex items-center gap-3">
                    {titleLockEnabled ? (
                      <Lock className="w-5 h-5 text-amber-500" />
                    ) : (
                      <LockOpen className="w-5 h-5 text-green-500" />
                    )}
                    <div>
                      <Label htmlFor="title-lock" className="text-sm font-medium">
                        Lock Title Editing
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        When enabled, users cannot change their profile title unless whitelisted
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="title-lock"
                    checked={titleLockEnabled}
                    onCheckedChange={handleTitleLockToggle}
                    data-testid="switch-title-lock"
                  />
                </div>

                {titleLockEnabled && (
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium">Whitelist Users</Label>
                      <p className="text-xs text-muted-foreground mb-3">
                        These users can still edit their title even when locked
                      </p>

                      <div className="flex gap-2">
                        <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                          <SelectTrigger className="flex-1" data-testid="select-whitelist-user">
                            <SelectValue placeholder="Select a user to whitelist" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableUsersForWhitelist.map(user => (
                              <SelectItem key={user.id} value={user.id}>
                                {user.firstName} {user.lastName} ({user.profile?.role || 'user'})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          size="icon"
                          onClick={handleAddWhitelistUser}
                          disabled={!selectedUserId}
                          data-testid="button-add-whitelist"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {whitelistedUsers.length > 0 && (
                      <div className="space-y-2">
                        <Label className="text-sm text-muted-foreground">
                          Whitelisted Users ({whitelistedUsers.length})
                        </Label>
                        <div className="flex flex-wrap gap-2">
                          {whitelistedUsers.map(userId => {
                            const user = getUserById(userId);
                            return (
                              <Badge
                                key={userId}
                                variant="secondary"
                                className="pl-2 pr-1 py-1 gap-1"
                              >
                                <span>
                                  {user ? `${user.firstName} ${user.lastName}` : userId}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-4 w-4 p-0 hover:bg-transparent"
                                  onClick={() => handleRemoveWhitelistUser(userId)}
                                  data-testid={`button-remove-whitelist-${userId}`}
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {whitelistedUsers.length === 0 && (
                      <div className="text-sm text-muted-foreground text-center py-4 border rounded-lg border-dashed">
                        No users whitelisted. All non-admin users cannot edit their title.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="fields" className="flex-1 overflow-y-auto mt-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Role</Label>
                  <p className="text-xs text-muted-foreground">
                    Configure which profile fields are visible/editable for each role
                  </p>
                </div>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger className="w-48" data-testid="select-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {fieldSettingsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">
                      Profile Fields for {ROLES.find(r => r.value === selectedRole)?.label}
                    </CardTitle>
                    <CardDescription>
                      <span className="flex items-center gap-4 text-xs">
                        <span className="flex items-center gap-1">
                          <EyeOff className="w-3 h-3" /> Hidden - Not shown
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3 text-blue-500" /> View Only - Can see but not edit
                        </span>
                        <span className="flex items-center gap-1">
                          <Pencil className="w-3 h-3 text-green-500" /> Editable - Full access
                        </span>
                      </span>
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {PROFILE_FIELDS.map(field => {
                      const FieldIcon = field.icon;
                      const currentVisibility = fieldSettings[field.key] || "editable";
                      
                      return (
                        <div
                          key={field.key}
                          className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-muted/50"
                        >
                          <div className="flex items-center gap-3">
                            <FieldIcon className="w-4 h-4 text-muted-foreground" />
                            <div>
                              <p className="text-sm font-medium">{field.label}</p>
                              <p className="text-xs text-muted-foreground">{field.description}</p>
                            </div>
                          </div>
                          <Select
                            value={currentVisibility}
                            onValueChange={(value) => handleFieldVisibilityChange(field.key, value as FieldVisibility)}
                          >
                            <SelectTrigger className="w-32" data-testid={`select-field-${field.key}`}>
                              <div className="flex items-center gap-2">
                                {getVisibilityIcon(currentVisibility)}
                                <span className="text-xs">{getVisibilityLabel(currentVisibility)}</span>
                              </div>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="hidden">
                                <div className="flex items-center gap-2">
                                  <EyeOff className="w-4 h-4" />
                                  <span>Hidden</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="visible">
                                <div className="flex items-center gap-2">
                                  <Eye className="w-4 h-4 text-blue-500" />
                                  <span>View Only</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="editable">
                                <div className="flex items-center gap-2">
                                  <Pencil className="w-4 h-4 text-green-500" />
                                  <span>Editable</span>
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
